import { t } from '@lingui/core/macro';

import SearchResultCard from '@rover/components/src/cards/SearchResultCard';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

import { shouldShowMaximalSearchCard } from '../../utilities/searchCardTreatment';
import SearchSectionHeader, { HeaderIconEnum, HeaderScenariosEnum } from '../SearchSectionHeader';

import MaximalSearchCardsGrid, { type MaximalGridItem } from './MaximalSearchCardsGrid';

type Props = {
  displayExpanded: boolean;
  isAuthenticated: boolean | null | undefined;
  locale: Locale;
  memberProfileLinkParams: Record<string, string | undefined>;
  onFavoriteToggled: (personOpk: string) => void;
  searchResults: SearchResult[];
  serviceName: string;
  serviceDescription: string;
  isSeoPage: boolean;
  preferCatReviews: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  showFromText: boolean;
  isShowingPriceInCurrency: boolean;
  cardTreatment: SearchCardTreatment;
};

export default ({
  displayExpanded,
  isAuthenticated,
  locale,
  memberProfileLinkParams,
  onFavoriteToggled,
  searchResults,
  serviceName,
  serviceDescription,
  isSeoPage,
  preferCatReviews,
  priceTransparencyLevel,
  showFromText,
  isShowingPriceInCurrency,
  cardTreatment,
}: Props): JSX.Element => {
  const renderResults = (): JSX.Element | JSX.Element[] => {
    const items: MaximalGridItem[] = searchResults.map((searchResult: SearchResult, i: number) => {
      const key = `alternate-${searchResult.personOpk}`;
      return {
        key,
        isFullWidth: false,
        node: (
          <SearchResultCard
            displayExpanded={displayExpanded}
            index={i}
            isAuthenticated={isAuthenticated}
            key={key}
            isAlternateResult
            locale={locale}
            memberProfileLinkParams={memberProfileLinkParams}
            onFavoriteToggled={() => onFavoriteToggled(searchResult.personOpk)}
            searchResult={searchResult}
            isSeoPage={isSeoPage}
            preferCatReviews={preferCatReviews}
            priceTransparencyLevel={priceTransparencyLevel}
            fromAlternateSearchResults
            showFromText={showFromText}
            isShowingPriceInCurrency={isShowingPriceInCurrency}
            cardTreatment={cardTreatment}
          />
        ),
      };
    });
    return shouldShowMaximalSearchCard(cardTreatment, isSeoPage) ? (
      <MaximalSearchCardsGrid items={items} />
    ) : (
      items.map((item) => item.node)
    );
  };

  return (
    <>
      <SearchSectionHeader
        id={HeaderScenariosEnum.LOW_SUPPLY}
        headerIconName={HeaderIconEnum.SEARCH}
        headerText={t({
          message: `Sitters who offer ${serviceName}`,
          comment: '{serviceName} e.g. Drop-in Visits',
        })}
        primarySubheaderText={serviceDescription}
      />
      {renderResults()}
    </>
  );
};
